/**
 * One tooltip for the whole console, in place of the browser's own grey box (owner 10/11: 「tooltip
 * 能不能不要用html原生的？看起来很丑」). Whatever carries a title keeps writing it as before: on hover —
 * or keyboard focus — the title moves to data-tip, so the browser has none to show, and this box shows it
 * above the element (below when there is no room), kept inside the window, in the charts' own look.
 *
 * A phone has no hover and never showed titles; nothing changes there. A control whose card or list is open
 * (aria-expanded) shows none: it sat over what it had opened — the paperclip's files, the emoji (QA R4-06).
 */

let box: HTMLDivElement | null = null;
let current: Element | null = null;
let timer = 0;
let installed = false;

const SELECTOR = "[title],[data-tip]";

function ensureBox(): HTMLDivElement {
  if (box && box.isConnected) return box;
  box = document.createElement("div");
  box.className = "lz-tip";
  box.setAttribute("role", "tooltip");
  document.body.appendChild(box);
  return box;
}

/** The element's words, its title moved to data-tip first; an icon with no words of its own keeps them as its label. */
function take(el: Element): string {
  const t = el.getAttribute("title");
  if (t !== null) {
    el.setAttribute("data-tip", t);
    el.removeAttribute("title");
    if (!el.hasAttribute("aria-label") && !(el.textContent ?? "").trim()) el.setAttribute("aria-label", t);
  }
  return el.getAttribute("data-tip") ?? "";
}

function hide(): void {
  window.clearTimeout(timer);
  current = null;
  box?.classList.remove("on");
}

function show(el: Element): void {
  const text = take(el).trim();
  if (!text || !el.isConnected || el.getAttribute("aria-expanded") === "true") {
    hide();
    return;
  }
  current = el;
  const b = ensureBox();
  b.textContent = text;
  b.classList.remove("on");
  b.style.left = "0px";
  b.style.top = "0px";
  const r = el.getBoundingClientRect();
  const w = b.offsetWidth;
  const h = b.offsetHeight;
  let top = r.top - h - 8;
  let below = false;
  if (top < 6) {
    top = r.bottom + 8;
    below = true;
  }
  const left = Math.max(6, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 6));
  b.style.left = `${Math.round(left)}px`;
  b.style.top = `${Math.round(top)}px`;
  b.dataset.below = below ? "1" : "";
  b.classList.add("on");
}

export function installTooltips(): void {
  if (installed) return;
  installed = true;
  document.addEventListener("pointerover", (e) => {
    if (e.pointerType !== "mouse") return;
    const el = (e.target as Element | null)?.closest?.(SELECTOR) ?? null;
    if (!el) {
      if (current) hide();
      return;
    }
    if (el === current) return;
    // At once, so the browser's own never starts its count.
    take(el);
    window.clearTimeout(timer);
    // Slow the first time, at once while moving from one to the next.
    timer = window.setTimeout(() => show(el), current ? 0 : 320);
  });
  document.addEventListener("pointerout", (e) => {
    const to = e.relatedTarget as Element | null;
    const from = (e.target as Element | null)?.closest?.(SELECTOR) ?? null;
    if (from && to && from.contains(to)) return;
    if (to?.closest?.(SELECTOR)) return; // pointerover takes it from here
    hide();
  });
  document.addEventListener("focusin", (e) => {
    const t = e.target as Element | null;
    if (!t?.matches?.(":focus-visible")) return;
    const el = t.closest(SELECTOR);
    if (el) show(el);
  });
  document.addEventListener("focusout", hide);
  window.addEventListener("scroll", hide, true);
  window.addEventListener("pointerdown", hide, true);
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape") hide();
  });
}
